/**
 * What a failed silent token acquisition means.
 *
 * Split out from `msal.ts` and kept free of any MSAL import so it can be tested
 * without a browser — the decision here is the whole difference between an app
 * that recovers a lapsed session and one that spins.
 *
 * The distinction that matters is **terminal vs transient**:
 *
 * - Terminal means the cached refresh token can no longer produce an access
 *   token. Retrying accomplishes nothing; only signing in again does. Left
 *   unclassified, this is the case that hangs — MSAL retries the token endpoint,
 *   the sync eventually reports a generic failure, and nothing tells the user
 *   that their session is what needs attention.
 * - Transient means the network was the problem. Re-authenticating here would
 *   throw the user out to Microsoft over a dropped connection, so these are
 *   rethrown and the sync retries later.
 *
 * Anything unrecognised is treated as terminal, deliberately. An unknown auth
 * failure that is really transient costs one sign-in; an unknown one treated as
 * transient costs a session that never recovers on its own, which is the bug
 * this exists to prevent.
 */

export type TokenFailure =
  /** MSAL says interaction is required — the definitive signal. */
  | 'interaction-required'
  /** The session is spent. Signing in again is the only fix. */
  | 'expired'
  /** The network, not the session. Worth retrying as-is. */
  | 'transient';

/**
 * MSAL error codes that mean "the request never reached the token endpoint".
 * Matched on `errorCode` rather than on class, so this module needs no MSAL
 * import and keeps working if the class hierarchy shifts under it.
 */
const TRANSIENT_CODES = new Set([
  'post_request_failed',
  'network_error',
  'no_network_connectivity',
  'request_timeout',
]);

/** Read `errorCode` off an unknown throwable without asserting a shape. */
function errorCodeOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { errorCode?: unknown }).errorCode;
  return typeof code === 'string' ? code : null;
}

function errorNameOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' ? name : null;
}

export function classifyTokenFailure(
  error: unknown,
  context: { online: boolean },
): TokenFailure {
  if (errorNameOf(error) === 'InteractionRequiredAuthError') return 'interaction-required';

  // Being offline explains any failure, whatever it claims to be.
  if (!context.online) return 'transient';

  const code = errorCodeOf(error);
  if (code !== null && TRANSIENT_CODES.has(code)) return 'transient';

  return 'expired';
}
